// API Configuration for production deployment
export const API_CONFIG = {
  // For development, use local backend. For Vercel deployment, use Render backend
  BASE_URL: import.meta.env.VITE_API_BASE_URL || 
           (typeof window !== 'undefined' && window.location.hostname === 'localhost' 
             ? 'http://localhost:5001' 
             : 'https://api.procurement.tectonixai.com'),
  // Check if we're on Vercel
  IS_VERCEL: typeof window !== 'undefined' && window.location.hostname.includes('vercel.app'),
  // Force direct API calls for now to debug
  FORCE_DIRECT_API: true,
};

// Mock API responses for when backend is unavailable  
const MOCK_API_RESPONSES = {
  '/api/health': { status: 'ok', timestamp: new Date().toISOString(), source: 'mock' },
  '/api/auth/logout': { success: true, redirectTo: '/login' },
  '/api/auth/user': {
    user: {
      id: 'admin-rongqi',
      email: 'rongqichen@hgconstruction.co.uk',
      firstName: 'Rongqi',
      lastName: 'Chen',
      profileImageUrl: null,
      permissionLevel: 'app_admin',
      authType: 'oauth',
      isEditor: true,
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    isAuthenticated: true
  }, // Real admin user for fallback
  // After successful login, this would be replaced with:
  '/api/auth/user_after_login': {
    id: 'app-admin-001',
    name: 'System Administrator', 
    email: 'admin@handoverhelper.com',
    picture: 'https://via.placeholder.com/100',
    role: 'App Admin',
    source: 'mock_authenticated'
  },
  '/api/autodesk/projects': {
    results: [
      {
        id: 'b.c53cca91-64e3-4f2d-b329-75d89031bc99',
        name: 'Argent - Tottenham Hale North Island',
        type: 'ACC Project',
        status: 'active',
        accountId: 'default-hub',
        hubName: 'HG Construction',
        createdDate: '2025-07-19T19:15:22.416Z',
        updatedDate: '2025-07-19T19:15:22.416Z'
      },
      {
        id: '8522ab92-dd7e-4478-901e-52d2ff851601',
        name: 'Argent-Tottenham',
        type: 'ACC Project',
        status: 'active',
        accountId: 'default-hub',
        hubName: 'HG Construction',
        createdDate: '2025-07-18T00:37:50.098Z',
        updatedDate: '2025-07-18T00:37:50.098Z'
      },
      {
        id: '48b36240-09fd-4bd1-89a4-5adf56332db6',
        name: 'Bridge Infrastructure Project',
        type: 'ACC Project',
        status: 'active',
        accountId: 'default-hub',
        hubName: 'HG Construction',
        createdDate: '2024-02-01T00:00:00.000Z',
        updatedDate: '2024-12-01T00:00:00.000Z'
      },
      {
        id: 'c53cca91-64e3-4f2d-b329-75d89031bc99',
        name: 'Metro Tower Construction',
        type: 'ACC Project',
        status: 'active',
        accountId: 'default-hub',
        hubName: 'HG Construction',
        createdDate: '2024-01-15T00:00:00.000Z',
        updatedDate: '2024-12-15T00:00:00.000Z'
      },
      {
        id: 'b.test-project-123',
        name: 'New Project',
        type: 'ACC Project',
        status: 'active',
        accountId: 'default-hub',
        hubName: 'HG Construction',
        createdDate: '2025-07-19T13:12:52.988Z',
        updatedDate: '2025-07-19T13:12:52.988Z'
      },
      {
        id: 'b.test-project-456',
        name: 'New Project',
        type: 'ACC Project',
        status: 'active',
        accountId: 'default-hub',
        hubName: 'HG Construction',
        createdDate: '2025-07-19T13:29:08.870Z',
        updatedDate: '2025-07-19T13:29:08.870Z'
      }
    ]
  },
  '/api/autodesk/account': {
    id: 'default-hub',
    name: 'HG Construction',
    websiteUrl: 'https://hgconstruction.co.uk',
    source: 'mock'
  }
};

// Helper function to get correct API URL
export function getApiUrl(path: string): string {
  if (path.startsWith('/api/')) {
    // For Vercel deployment, use API proxy
    if (API_CONFIG.IS_VERCEL && !API_CONFIG.FORCE_DIRECT_API) {
      return path; // Use relative path to trigger Vercel API proxy
    }
    return API_CONFIG.BASE_URL + path;
  }
  return path;
}

// Setup global fetch override for production with fallback
if (typeof window !== 'undefined') {
  const originalFetch = window.fetch;
  
  window.fetch = async (input, init = {}) => {
    let finalInput = input;
    let finalInit = init;
    
    if (typeof input === 'string' && input.startsWith('/api/')) {
      // For Vercel deployment, use API proxy
      if (API_CONFIG.IS_VERCEL && !API_CONFIG.FORCE_DIRECT_API) {
        finalInput = input; // Use relative path to trigger Vercel API proxy
      } else {
        finalInput = API_CONFIG.BASE_URL + input;
      }
      // Check if this is an admin API request
      const isAdminAPI = finalInput.includes('/api/admin/');
      
      if (isAdminAPI) {
        // For admin API requests, add Authorization header with JWT token
        const getStoredToken = () => {
          try {
            return localStorage.getItem('adminToken');
          } catch (error) {
            try {
              return sessionStorage.getItem('adminToken_session');
            } catch (sessionError) {
              return null;
            }
          }
        };
        
        const token = getStoredToken();
        if (token) {
          finalInit = { 
            ...init, 
            headers: {
              ...init.headers,
              'Authorization': `Bearer ${token}`
            }
          };
        } else {
          finalInit = { ...init };
        }
      } else {
        // For regular API requests, use credentials
        finalInit = { ...init, credentials: 'include' };
      }
      console.log('🔄 API redirect:', input, '→', finalInput);
      console.log('🔧 Request config:', { 
        url: finalInput, 
        method: finalInit.method || 'GET',
        credentials: finalInit.credentials,
        headers: finalInit.headers 
      });
    }
    
    try {
      const response = await originalFetch(finalInput, finalInit);
      console.log('📡 Response received:', {
        url: finalInput,
        status: response.status,
        statusText: response.statusText,
        headers: {
          'content-type': response.headers.get('content-type'),
          'access-control-allow-origin': response.headers.get('access-control-allow-origin'),
          'access-control-allow-credentials': response.headers.get('access-control-allow-credentials')
        }
      });
      
      // If we get a 503, use mock response immediately
      if (response.status === 503 && typeof input === 'string' && input.startsWith('/api/')) {
        console.log('⚠️ Primary URL failed (503), using mock response for:', input);
        const mockData = MOCK_API_RESPONSES[input];
        if (mockData) {
          console.log('✅ Mock response available for:', input);
          return new Response(JSON.stringify(mockData), {
            status: 200,
            statusText: 'OK',
            headers: {
              'Content-Type': 'application/json',
              'X-Mock-Response': 'true'
            }
          });
        }
      }
      
      return response;
    } catch (error) {
      // 提供更精确的错误信息
      let errorType = 'Unknown error';
      if (error.message.includes('Failed to fetch') || error.message.includes('ERR_CONNECTION_REFUSED')) {
        errorType = 'Backend service not running (localhost:5001)';
      } else if (error.message.includes('NetworkError')) {
        errorType = 'Network connection error';
      } else if (error.message.includes('timeout')) {
        errorType = 'Request timeout';
      }
      
      console.error('❌ Fetch error:', {
        url: finalInput,
        error: error.message,
        errorType: errorType,
        stack: error.stack
      });
      
      // Use mock response for network failures
      if (typeof input === 'string' && input.startsWith('/api/')) {
        console.log('⚠️ Network error, using mock response for:', input);
        const mockData = MOCK_API_RESPONSES[input];
        if (mockData) {
          console.log('✅ Mock response available for:', input);
          return new Response(JSON.stringify(mockData), {
            status: 200,
            statusText: 'OK',
            headers: {
              'Content-Type': 'application/json',
              'X-Mock-Response': 'true'
            }
          });
        } else {
          console.log('❌ No mock data available for:', input);
        }
      }
      
      throw error;
    }
  };
  console.log('🚀 Global API setup complete.');
  console.log('🎯 Primary URL:', API_CONFIG.BASE_URL);
  console.log('🌍 Current origin:', window.location.origin);
  console.log('🔧 Is Vercel deployment:', API_CONFIG.IS_VERCEL);
}